import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useAgentSessions } from "contexts/AgentSessions";
import { useState } from "react";

import { trackEvent } from "utils/tracking";

import { BrandSearchResult } from "..";
import MoreInfoDialog from "./MoreInfoDialog";
import BrandHeader from "./header";

export default function BrandCard({
  brand,
  isLoading,
  isAdded,
  addBrand,
  isScheduled = false,
}: {
  brand: BrandSearchResult;
  isAdded: boolean;
  isLoading: boolean;
  isScheduled?: boolean;
  addBrand: (brand: BrandSearchResult) => void;
}) {
  const { selectedAgentSessionId } = useAgentSessions();
  const [brandCardDialog, setBrandCardDialog] = useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${grey[300]}`,
        backgroundColor: "white",
      }}
    >
      <CardContent>
        <BrandHeader brand={brand} />
        <Box display="flex" justifyContent="flex-end" gap={0.5}>
          {brand.google_maps_uri && (
            <IconButton
              component={Link}
              href={brand.google_maps_uri}
              onClick={() => {
                trackEvent("Agent Chat Brand Google Maps Link Pressed", {
                  "Agent Session ID": selectedAgentSessionId,
                  "Google Place Brand ID": brand.google_maps_uri,
                  "Brand Website": brand.website,
                  "Maps Link": brand.google_maps_uri,
                });
              }}
              target="blank"
              size="small"
              sx={{
                textTransform: "none",
                color: grey[600],
                fontSize: 12,
              }}
            >
              <Box
                component="span"
                className="fa-solid fa-map"
                fontSize="14px"
              />
            </IconButton>
          )}
          {brand.bento_brand_id && brand.source === "database" && (
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              sx={{
                textTransform: "none",
                color: grey[600],
                fontSize: 12,
              }}
              onClick={() => {
                trackEvent("Agent Chat Brand More Info Pressed", {
                  "Agent Session ID": selectedAgentSessionId,
                  "Bento Brand ID": brand.bento_brand_id,
                  "Brand Website": brand.website,
                });
                setBrandCardDialog(true);
              }}
            >
              More Info{" "}
              <Box
                component="span"
                className="fa-solid fa-up-right-from-square"
                sx={{ ml: 0.75 }}
              />
            </Button>
          )}
          {brand.website && brand.source !== "database" && (
            <Button
              variant="outlined"
              component={Link}
              target="blank"
              href={brand.website}
              size="small"
              color="inherit"
              sx={{
                textTransform: "none",
                color: grey[600],
                fontSize: 12,
              }}
              onClick={() => {
                trackEvent("Agent Chat Brand Website Pressed", {
                  "Agent Session ID": selectedAgentSessionId,
                  "Bento Brand ID": brand.bento_brand_id,
                  "Google Place Brand ID": brand.google_place_brand_id,
                  "Brand Website": brand.website,
                });
              }}
            >
              Website{" "}
              <Box
                component="span"
                className="fa-solid fa-up-right-from-square"
                sx={{ ml: 0.75 }}
              />
            </Button>
          )}
          {!isScheduled && (
            <LoadingButton
              disableElevation
              loading={isLoading}
              variant={isAdded ? "outlined" : "contained"}
              sx={{ textTransform: "none" }}
              size="small"
              onClick={() => {
                addBrand(brand);
              }}
            >
              {isAdded ? "Remove" : "Pitch"}{" "}
              <Box
                component="span"
                className={`fa-solid fa-${isAdded ? "minus" : "plus"}`}
                sx={{ ml: 0.75 }}
              />
            </LoadingButton>
          )}
          {isScheduled && (
            <Button
              disableElevation
              variant="contained"
              disabled
              sx={{ textTransform: "none" }}
              size="small"
            >
              Scheduled
            </Button>
          )}
        </Box>
      </CardContent>
      {brandCardDialog && brand.bento_brand_id && (
        <MoreInfoDialog
          open={brandCardDialog}
          onClose={() => {
            setBrandCardDialog(false);
          }}
          brandDetailsId={brand.bento_brand_id}
        />
      )}
    </Card>
  );
}
